import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PickupLoan } from './types';
import {
  buildPickupAnalysisFilterCounts,
  PICKUP_ANALYSIS_DEFAULT_FILTERS,
  type PickupAnalysisFilters,
  type PickupAnalysisSegment,
  type PickupGainFilter,
  type PickupVerificationFilter
} from './pickupAnalysisWorkspace';

interface PickupAnalysisFilterRailProps {
  segment: PickupAnalysisSegment;
  onSegmentChange: (segment: PickupAnalysisSegment) => void;
  segmentCounts: Record<PickupAnalysisSegment, number>;
  loans: PickupLoan[];
  filters: PickupAnalysisFilters;
  onFiltersChange: (filters: PickupAnalysisFilters) => void;
}

const SEGMENT_OPTIONS: Array<{ value: PickupAnalysisSegment; label: string }> = [
  { value: 'active', label: 'Active locks' },
  { value: 'expired', label: 'Expired' },
  { value: 'funded', label: 'Funded' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'history', label: 'Run history' }
];

const GAIN_OPTIONS: Array<{ value: Exclude<PickupGainFilter, 'all'>; label: string }> = [
  { value: 'trigger', label: 'Pickup ≥ 0.500' },
  { value: 'positive', label: 'Positive < 0.500' },
  { value: 'none', label: 'No pickup / unpriced' }
];

const VERIFICATION_OPTIONS: Array<{
  value: Exclude<PickupVerificationFilter, 'all'>;
  label: string;
  dotClassName: string;
}> = [
  { value: 'none', label: 'None', dotClassName: 'bg-success' },
  { value: 'minor', label: 'Minor', dotClassName: 'bg-warning' },
  { value: 'major', label: 'Major', dotClassName: 'bg-destructive' },
  { value: 'not_checked', label: 'Not checked', dotClassName: 'bg-muted-foreground' }
];

function RailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-1.5">
      <h3 className="mb-2 px-1 text-[9.5px] font-semibold uppercase leading-none tracking-[0.13em] text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function RailCount({ count, className }: { count: number; className?: string }) {
  return (
    <span className={cn('ml-auto shrink-0 tabular-nums text-muted-foreground', className)}>
      {count}
    </span>
  );
}

export function PickupAnalysisFilterRail({
  segment,
  onSegmentChange,
  segmentCounts,
  loans,
  filters,
  onFiltersChange
}: PickupAnalysisFilterRailProps) {
  const counts = buildPickupAnalysisFilterCounts(loans, filters);

  return (
    <aside
      className="flex min-h-[32rem] w-full flex-col overflow-hidden border-r bg-card lg:w-[248px]"
      aria-label="Pick Up Analysis filters"
      data-pickup-analysis-rail
    >
      <div className="flex-1 space-y-[18px] overflow-y-auto px-3.5 py-4">
        <RailSection title="Segment">
          <div className="space-y-0.5">
            {SEGMENT_OPTIONS.map((option) => {
              const active = segment === option.value;
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant="ghost"
                  size="xs"
                  aria-pressed={active}
                  className={cn(
                    'w-full justify-start',
                    active
                      ? 'bg-primary font-semibold text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  )}
                  onClick={() => onSegmentChange(option.value)}
                >
                  <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
                  <RailCount
                    count={segmentCounts[option.value]}
                    className={active ? 'text-primary-foreground/80' : undefined}
                  />
                </Button>
              );
            })}
          </div>
        </RailSection>

        <RailSection title="Pickup">
          <div className="space-y-1">
            {GAIN_OPTIONS.map((option) => {
              const active = filters.gain === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  className="flex h-7 w-full items-center gap-2.5 rounded-md px-1 text-left text-[11.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => onFiltersChange({
                    ...filters,
                    gain: active ? 'all' : option.value
                  })}
                >
                  <span
                    className={cn(
                      'h-[13px] w-[13px] shrink-0 rounded-[3px] border-[1.5px]',
                      active ? 'border-primary bg-primary' : 'border-muted-foreground/60 bg-card'
                    )}
                    aria-hidden="true"
                  />
                  <span className="min-w-0 truncate">{option.label}</span>
                  <RailCount count={counts.gain[option.value]} />
                </button>
              );
            })}
          </div>
        </RailSection>

        <RailSection title="Verification">
          <div className="space-y-1">
            {VERIFICATION_OPTIONS.map((option) => {
              const active = filters.verification === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  className={cn(
                    'flex h-7 w-full items-center gap-2.5 rounded-md px-1.5 text-left text-[11.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active && 'bg-muted font-semibold'
                  )}
                  onClick={() => onFiltersChange({
                    ...filters,
                    verification: active ? 'all' : option.value
                  })}
                >
                  <span className={cn('h-[7px] w-[7px] shrink-0 rounded-full', option.dotClassName)} aria-hidden="true" />
                  <span className="min-w-0 truncate">{option.label}</span>
                  <RailCount count={counts.verification[option.value]} />
                </button>
              );
            })}
          </div>
        </RailSection>

        <RailSection title="Investor">
          <div className="space-y-1">
            {counts.investors.length === 0 ? (
              <p className="px-1.5 py-1 text-[11.5px] text-muted-foreground">No investors in this segment</p>
            ) : counts.investors.map(({ investor, count }) => {
              const active = filters.investor === investor;
              return (
                <button
                  key={investor}
                  type="button"
                  aria-pressed={active}
                  className={cn(
                    'flex h-7 w-full items-center gap-2 rounded-md px-1.5 text-left text-[11.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active && 'bg-muted font-semibold text-primary'
                  )}
                  onClick={() => onFiltersChange({
                    ...filters,
                    investor: active ? 'all' : investor
                  })}
                >
                  <span className="min-w-0 flex-1 truncate">{investor}</span>
                  <RailCount count={count} />
                </button>
              );
            })}
          </div>
        </RailSection>
      </div>

      <div className="border-t bg-card p-3.5">
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="w-full bg-background"
          onClick={() => {
            onSegmentChange('active');
            onFiltersChange(PICKUP_ANALYSIS_DEFAULT_FILTERS);
          }}
        >
          Clear filters
        </Button>
      </div>
    </aside>
  );
}
